<template>
  <div class="image-slider-container" ref="sliderContainer">
    <div class="slider-row" ref="topRow">
      <div
        class="slider-content"
        ref="topSlider"
        :style="{
          transform: `translateX(${topOffset}px)`,
          gap: `${cardGap}px`,
        }"
        @mouseenter="pauseAutoScroll"
        @mouseleave="resumeAutoScroll"
      >
        <div
          class="image-card"
          v-for="(image, index) in topImages"
          :key="'top-' + index"
          :style="{ width: `${cardWidth}px` }"
          @mouseenter="pauseForCard"
          @mouseleave="resumeAfterCard"
          @click="openPreview(image)"
        >
          <OssImg class="card" :src="image.url" :lazy="false" :alt="image.name" fit="contain" />
          <div class="image-info">{{ image.name }}</div>
        </div>
      </div>
    </div>

    <div class="slider-row bottom-row" ref="bottomRow">
      <div
        class="slider-content"
        ref="bottomSlider"
        :style="{
          transform: `translateX(${bottomOffset}px)`,
          gap: `${cardGap}px`,
        }"
        @mouseenter="pauseAutoScroll"
        @mouseleave="resumeAutoScroll"
      >
        <div
          class="image-card"
          v-for="(image, index) in bottomImages"
          :key="'bottom-' + index"
          :style="{ width: `${cardWidth}px` }"
          @mouseenter="pauseForCard"
          @mouseleave="resumeAfterCard"
          @click="openPreview(image)"
        >
          <OssImg class="card" :src="image.url" :lazy="false" :alt="image.name" fit="contain" />
          <div class="image-info">{{ image.name }}</div>
        </div>
      </div>
    </div>

    <!-- 图片预览弹窗 -->
    <el-dialog
      v-model="previewVisible"
      title="资质证书"
      width="50%"
      :append-to-body="true"
      :destroy-on-close="true"
      class="preview-dialog"
    >
      <div class="preview-content">
        <OssImg :src="currentImage.url" :lazy="false" :alt="currentImage.name" fit="contain" />
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick, watch } from "vue";

// 图片数据
const topImages = ref([]);
const bottomImages = ref([]);

// 滚动控制
const topOffset = ref(0);
const bottomOffset = ref(0);
const isPaused = ref(false);
let animationFrame = null;

// 暂停状态
const manualPause = ref(false);
const pauseTimeout = ref(null);

// 自适应相关变量
const sliderContainer = ref(null);
const topRow = ref(null);
const bottomRow = ref(null);
const screenWidth = ref(window.innerWidth);
const cardGap = ref(24);
const cardWidth = ref(365);
const maxVisible = ref(5);
const minCardWidth = 120;
const originalImageCount = 9; // 原始图片数量

// 图片预览
const previewVisible = ref(false);
const currentImage = ref({ url: "", name: "" });

// 打开图片预览
const openPreview = (image) => {
  currentImage.value = image;
  previewVisible.value = true;
};

// 初始化图片数据 - 为上下排使用不同的复制策略
const initImageData = () => {
  // 新增资质图片（放在前面）
  const newImagesTop = [
    "1780281282723_7519.jpg",
    "1780281283104_7446.jpg",
    "1780281283386_3728.jpg",
    "1780281283858_3529.jpg",
    "1780281284075_3937.jpg",
    "1780281284509_2769.jpg",
  ].map(file => ({ url: file, name: "资质证书" }));

  const newImagesBottom = [
    "1780281284707_1369.jpg",
    "1780281284925_7438.jpg",
    "1780281285122_12.jpg",
    "1780281285258_4483.jpg",
    "1780281285378_8616.png",
  ].map(file => ({ url: file, name: "资质证书" }));

  // 原有图片
  let images_t = [...newImagesTop];
  for (let i = 1; i <= originalImageCount; i++) {
    if (i === 6) continue; // 跳过 honor6.png
    images_t.push({
      url: `//UIV3/honor${i}.png`,
      name: "资质证书",
    });
  }

  let images_b = [...newImagesBottom];
  for (let i = 1; i <= originalImageCount; i++) {
    images_b.push({
      url: `//UIV3/honor${50 + i}.png`,
      name: "资质证书",
    });
  }

  // 上排：[复制, 原始, 复制] - 适合向左滚动
  topImages.value = [...images_t, ...images_t, ...images_t];

  // 下排：[原始, 复制, 原始] - 专为向右滚动优化
  bottomImages.value = [...images_b, ...images_b, ...images_b];
};

// 计算图片宽度和滚动参数
const calculateLayout = () => {
  if (!topRow.value) return;

  // 获取轮播容器宽度（实际可展示区域）
  const containerWidth = topRow.value.offsetWidth - 40; // 减去左右内边距

  // 计算最多可展示数量
  let visibleCount = maxVisible.value;
  let calculatedWidth = containerWidth / visibleCount - cardGap.value;

  // 处理小屏幕情况
  if (calculatedWidth < minCardWidth) {
    visibleCount = Math.floor(containerWidth / (minCardWidth + cardGap.value));
    visibleCount = Math.max(visibleCount, 1);
    calculatedWidth = containerWidth / visibleCount - cardGap.value;
    maxVisible.value = visibleCount;
  }

  cardWidth.value = Math.max(calculatedWidth, minCardWidth);
};

// 计算滚动参数（步长和总宽度）
const getScrollParams = () => {
  const step = cardWidth.value + cardGap.value; // 单个卡片宽度+间隙
  const totalWidth = step * topImages.value.length / 3; // 实际图片组总宽度（除以3因为复制了3份）
  return { step, totalWidth };
};

// 无限滚动动画函数
const animateScroll = () => {
  if (!isPaused.value && !manualPause.value) {
    const { totalWidth } = getScrollParams();
    const scrollSpeed = 1; // 滚动速度

    // 上侧轮播：向左匀速滚动
    topOffset.value -= scrollSpeed;
    // 当滚动完一组原始图片后，无缝切换回起始位置
    if (topOffset.value <= -totalWidth * 2) {
      topOffset.value = -totalWidth;
    }

    // 下侧轮播：向右匀速滚动 - 全新的无缝滚动逻辑
    bottomOffset.value += scrollSpeed;
    // 当滚动超过一组原始图片宽度时，无缝衔接
    if (bottomOffset.value >= 0) {
      // 关键修复：不是重置为0，而是减去一个总宽度，保持视觉连贯性
      bottomOffset.value -= totalWidth;
    }
  }

  // 继续请求下一帧动画
  animationFrame = requestAnimationFrame(animateScroll);
};

// 开始滚动动画
const startAnimation = () => {
  if (animationFrame) cancelAnimationFrame(animationFrame);
  animateScroll();
};

// 暂停/恢复滚动
const pauseAutoScroll = () => {
  manualPause.value = true;
};

const resumeAutoScroll = () => {
  clearTimeout(pauseTimeout.value);
  manualPause.value = false;
};

const pauseForCard = () => {
  manualPause.value = true;
};

const resumeAfterCard = () => {
  clearTimeout(pauseTimeout.value);
  pauseTimeout.value = setTimeout(() => {
    manualPause.value = false;
  }, 1000);
};

// 监听窗口变化
const handleResize = () => {
  screenWidth.value = window.innerWidth;
  calculateLayout();
  // 重新计算偏移量以保持视觉连贯性
  const { totalWidth } = getScrollParams();
  topOffset.value = -totalWidth; // 上排初始位置
  bottomOffset.value = -totalWidth; // 下排初始位置
};

// 生命周期钩子
onMounted(() => {
  initImageData();
  nextTick(() => {
    calculateLayout();
    const { totalWidth } = getScrollParams();
    // 初始位置设置
    topOffset.value = -totalWidth; // 上排从第一组复制内容末尾开始
    bottomOffset.value = -totalWidth; // 下排从原始内容开始
    startAnimation();
    window.addEventListener("resize", handleResize);
  });
});

onUnmounted(() => {
  if (animationFrame) cancelAnimationFrame(animationFrame);
  window.removeEventListener("resize", handleResize);
  if (pauseTimeout.value) clearTimeout(pauseTimeout.value);
});

// 监听屏幕宽度变化
watch(screenWidth, handleResize);
</script>

<style lang="scss" scoped>
.image-slider-container {
  width: 100%;
  padding: 30px;
  box-sizing: border-box;
  overflow: hidden;
  user-select: none;
}

.slider-row {
  width: 100%;
  height: 15vh;
  min-height: 200px;
  position: relative;
  overflow: hidden;
  margin-bottom: 30px;
  border-radius: 10px;
  padding: 0 20px;
  box-sizing: border-box;
}

.slider-content {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  display: flex;
  align-items: center;
  // 确保内容足够宽，不会出现空白
  width: fit-content;
  min-width: 100%;
}

.image-card {
  width: 100%;
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  position: relative;
  flex-shrink: 0;
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }

  .card {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .image-info {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.6);
    color: white;
    padding: 8px;
    font-size: 12px;
    text-align: center;
    transform: translateY(100%);
    transition: transform 0.3s ease;
  }

  &:hover .image-info {
    transform: translateY(0);
  }
}

// 预览弹窗样式
:deep(.preview-dialog) {
  .el-dialog__body {
    padding: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
  }
}

.preview-content {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    max-width: 100%;
    max-height: 70vh;
    object-fit: contain;
  }
}
</style>
